<template>
  <section class="create-product">
    <!-- Error Modal -->
    <div v-if="showErrorModal" class="error-modal-overlay">
      <div class="error-modal-content">
        <div class="error-modal-header">
          <i class="fa-solid fa-circle-exclamation error-modal-icon"></i>
          <h3>Product Creation Failed</h3>
          <button @click="closeErrorModal" class="error-modal-close">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div class="error-modal-body">
          <p>{{ errorMessage }}</p>
          <div class="error-actions">
            <button @click="retryCreate" class="error-retry-btn">
              <i class="fa-solid fa-rotate-right"></i> Try Again
            </button>
            <button @click="closeErrorModal" class="error-close-btn">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>

    <transition name="fade">
      <!-- ✅ Success Modal State -->
      <div v-if="productCreated" class="ack-container">
        <div class="success-animation">
          <div class="success-checkmark">
            <i class="fa-solid fa-check"></i>
          </div>
          <div class="success-ripple"></div>
          <div class="success-ripple delay-1"></div>
          <div class="success-ripple delay-2"></div>
        </div>
        <h2>
          <i class="fa-solid fa-circle-check" style="color: #4caf50; margin-right: 0.5rem;"></i>
          Product Successfully Created!
        </h2>
        <p>Your new product has been added to the store and is now available for customers.</p>
        <div class="success-details">
          <div class="success-detail-item">
            <i class="fa-solid fa-check-circle"></i>
            <span>Product saved to database</span>
          </div>
          <div class="success-detail-item">
            <i class="fa-solid fa-check-circle"></i>
            <span>Images uploaded successfully</span>
          </div>
          <div class="success-detail-item">
            <i class="fa-solid fa-check-circle"></i>
            <span>Ready for customer viewing</span>
          </div>
        </div>
        <div class="success-actions">
          <button class="continue-btn" @click="resetForm">
            <i class="fa-solid fa-plus-circle"></i> Create Another Product
          </button>
          <button class="view-btn" @click="goToProducts">
            <i class="fa-solid fa-eye"></i> View All Products
          </button>
        </div>
      </div>

      <!-- 🧾 Form State -->
      <div v-else class="form-wrapper">
        <div class="form-header">
          <h1>Create New Product</h1>
          <p class="form-subtitle">Fill in the details below to add a new product to your store</p>
          <p v-if="!canCreateProduct" class="permission-warning">
            <i class="fa-solid fa-lock"></i> Only support and super admin can create products. Your role does not have permission.
          </p>
        </div>
        
        <form @submit.prevent="createProduct" class="product-form">
          <!-- === BASIC INFO === -->
          <div class="form-section">
            <div class="section-header">
              <i class="fa-solid fa-info-circle"></i>
              <h3>Basic Information</h3>
            </div>
            <div class="form-grid">
              <div
                class="form-group"
                v-for="(field, index) in basicFields"
                :key="index"
              >
                <label :for="field.model">
                  {{ field.label }}
                  <span v-if="field.required" class="required">*</span>
                </label>
                <select
                  v-if="field.type === 'select' && field.model === 'sub_category'"
                  v-model="form[field.model]"
                  :id="field.model"
                  :disabled="isLoading || !canCreateProduct"
                >
                  <option value="">Select subcategory (optional)</option>
                  <option
                    v-for="(displayName, key) in subcategories"
                    :key="key"
                    :value="key"
                  >
                    {{ displayName }}
                  </option>
                </select>
                <select
                  v-else-if="field.model === 'brand' && isFootballFootwear"
                  v-model="form.brand"
                  :id="field.model"
                  required
                  :disabled="isLoading || !canCreateProduct"
                >
                  <option value="">Select brand</option>
                  <option
                    v-for="brandName in footwearBrands"
                    :key="brandName"
                    :value="brandName"
                  >
                    {{ brandName }}
                  </option>
                </select>
                <input
                  v-else-if="field.type !== 'textarea' && field.type !== 'select'"
                  v-model="form[field.model]"
                  :type="field.type"
                  :placeholder="field.placeholder || ''"
                  :maxlength="field.maxlength || null"
                  :required="field.required"
                  :disabled="isLoading || !canCreateProduct"
                />
                <textarea
                  v-else-if="field.type === 'textarea'"
                  v-model="form[field.model]"
                  rows="4"
                  :disabled="isLoading || !canCreateProduct"
                ></textarea>
              </div>
              <div class="form-group">
                <label for="category_id">Category <span class="required">*</span></label>
                <select
                  id="category_id"
                  v-model="form.category_id"
                  required
                  :disabled="isLoading || !canCreateProduct"
                >
                  <option value="">Select sport category</option>
                  <option
                    v-for="cat in categories"
                    :key="cat.id"
                    :value="cat.id"
                  >
                    {{ cat.name }}
                  </option>
                </select>
              </div>
              <div v-if="isFootballFootwear" class="form-group">
                <label for="surface_type">Surface Type <span class="required">*</span></label>
                <select
                  id="surface_type"
                  v-model="form.surface_type"
                  required
                  :disabled="isLoading || !canCreateProduct"
                >
                  <option value="">Select surface</option>
                  <option value="TF">Turf (TF)</option>
                  <option value="FG">Firm Ground / Grass (FG)</option>
                </select>
              </div>
            </div>
          </div>

          <!-- === PRICING === -->
          <div class="form-section">
            <div class="section-header">
              <i class="fa-solid fa-tag"></i>
              <h3>Pricing</h3>
            </div>
            <div class="form-grid">
              <div
                v-for="(field, index) in pricingFields"
                :key="index"
                class="form-group"
              >
                <label>
                  {{ field.label }}
                  <span v-if="field.required" class="required">*</span>
                </label>
                <div class="input-with-icon" v-if="field.model === 'currency'">
                  <select v-model="form[field.model]" :disabled="isLoading || !canCreateProduct">
                    <option value="KES">KES (Kenyan Shilling)</option>
                    <option value="USD">USD (US Dollar)</option>
                    <option value="EUR">EUR (Euro)</option>
                  </select>
                  <i class="fa-solid fa-money-bill-wave"></i>
                </div>
                <div class="input-with-icon" v-else>
                  <input
                    v-model="form[field.model]"
                    :type="field.type"
                    step="0.01"
                    :required="field.required || false"
                    :disabled="isLoading"
                  />
                  <i class="fa-solid fa-coins"></i>
                </div>
              </div>
            </div>
          </div>

          <!-- === INVENTORY === -->
          <div class="form-section">
            <div class="section-header">
              <i class="fa-solid fa-boxes-stacked"></i>
              <h3>Inventory</h3>
            </div>
            <div class="form-grid">
              <div
                v-for="(field, index) in inventoryFields"
                :key="index"
                class="form-group"
              >
                <template v-if="field.type === 'checkbox'">
                  <div class="checkbox-wrapper">
                    <input
                      v-model="form[field.model]"
                      type="checkbox"
                      :id="field.model"
                      :disabled="isLoading"
                    />
                    <label :for="field.model" class="checkbox-label">
                      <span class="checkmark"></span>
                      {{ field.label }}
                    </label>
                  </div>
                </template>
                <template v-else>
                  <label>
                    {{ field.label }}
                    <span v-if="field.required" class="required">*</span>
                  </label>
                  <input 
                    v-model="form[field.model]" 
                    :type="field.type" 
                    :disabled="isLoading"
                  />
                </template>
              </div>
            </div>
          </div>

          <!-- === MEDIA === -->
          <div class="form-section">
            <div class="section-header">
              <i class="fa-solid fa-images"></i>
              <h3>Media</h3>
            </div>
            <div class="form-grid">
              <div class="form-group">
                <label>Thumbnail Image <span class="required">*</span></label>
                <div class="file-upload-area" @click="triggerThumbnailUpload" :class="{ 'disabled': isLoading || !canCreateProduct }">
                  <i class="fa-solid fa-cloud-arrow-up"></i>
                  <p v-if="!selectedThumbnail">Click to upload thumbnail</p>
                  <p v-else class="file-name">{{ selectedThumbnail.name }} ({{ formatFileSize(selectedThumbnail.size) }})</p>
                  <input 
                    type="file" 
                    ref="thumbnailInput" 
                    @change="onThumbnailChange" 
                    accept="image/*"
                    :disabled="isLoading"
                    hidden
                  />
                </div>
                <p class="upload-hint">Max {{ maxFileSizeLabel }}.</p>
              </div>
              <div class="form-group">
                <label>Additional Images</label>
                <div class="file-upload-area" @click="triggerImagesUpload" :class="{ 'disabled': isLoading || !canCreateProduct }">
                  <i class="fa-solid fa-cloud-arrow-up"></i>
                  <p v-if="selectedImages.length === 0">Click to upload images</p>
                  <p v-else class="file-name">{{ selectedImages.length }} file(s) selected</p>
                  <input 
                    type="file" 
                    ref="imagesInput" 
                    @change="onImagesChange" 
                    accept="image/*"
                    multiple
                    :disabled="isLoading"
                    hidden
                  />
                </div>
                <p class="upload-hint">
                  <template v-if="isFootwear">
                    Optional. For footwear colorways in any sport, name files like
                    <code>name-#222223-#d3e9fb-top</code>
                    (views: main, top, bottom, pair). Up to {{ maxImageCount }} images,
                    {{ maxFileSizeLabel }} each. Plain images are fine if there is only one color.
                  </template>
                  <template v-else-if="isApparel">
                    Optional. For apparel colors in any sport, name each file
                    <code>nameofapparel-#primarycolor-undressed</code> or
                    <code>nameofapparel-#primarycolor-dressed</code>
                    (for example <code>training-tee-#1a1a1a-undressed</code>).
                    Every color needs both views. Up to {{ maxApparelColors }} colors,
                    {{ maxFileSizeLabel }} each. Plain images are fine if there is only one color.
                  </template>
                  <template v-else>
                    Up to {{ maxImageCount }} images, {{ maxFileSizeLabel }} each.
                  </template>
                </p>
              </div>
            </div>
            <p class="upload-summary">
              Total upload: {{ uploadSizeSummary }}
            </p>
          </div>

          <!-- === OTHER DETAILS === -->
          <div class="form-section">
            <div class="section-header">
              <i class="fa-solid fa-gear"></i>
              <h3>Additional Details</h3>
            </div>
            <div class="form-grid">
              <div
                v-for="(field, index) in otherFields"
                :key="index"
                class="form-group"
              >
                <template v-if="field.type === 'checkbox'">
                  <div class="checkbox-wrapper">
                    <input
                      v-model="form[field.model]"
                      type="checkbox"
                      :id="field.model"
                      :disabled="isLoading"
                    />
                    <label :for="field.model" class="checkbox-label">
                      <span class="checkmark"></span>
                      {{ field.label }}
                    </label>
                  </div>
                </template>
                <template v-else>
                  <label>{{ field.label }}</label>
                  <input
                    v-model="form[field.model]"
                    :type="field.type"
                    :placeholder="field.placeholder || ''"
                    :disabled="isLoading"
                  />
                </template>
              </div>
            </div>
          </div>

          <!-- ✅ Submit Button -->
          <div class="form-actions">
            <button 
              type="submit" 
              class="submit-btn" 
              :class="{ 'loading': isLoading }"
              :disabled="isLoading || !canCreateProduct"
            >
              <template v-if="!isLoading">
                <i class="fa-solid fa-plus-circle"></i>
                Create Product
              </template>
              <template v-else>
                <i class="fa-solid fa-spinner fa-spin"></i>
                Creating Product...
              </template>
            </button>
            <button 
              type="button" 
              class="cancel-btn" 
              @click="resetForm" 
              :disabled="isLoading"
            >
              Clear Form
            </button>
          </div>
        </form>
      </div>
    </transition>
  </section>
</template>

<script>
/**
 * CreateProduct Component
 * 
 * A comprehensive product creation form for admin users with the following features:
 * - Multi-section form (Basic Info, Pricing, Inventory, Media, Additional Details)
 * - File upload support for thumbnail and multiple images
 * - HMAC signature generation for secure API communication
 * - Role-based access control (support and super_admin only)
 * - Comprehensive error handling and user feedback
 * - Success modal with animation
 * - Responsive design for all device sizes
 * 
 * @component
 * @requires @/utils/utils - generateHmacSignature function
 * @requires @/config/api - buildApiUrl function
 * @requires @/authen/adminAuth - useAdminAuthStore for authentication
 * 
 * @emits {string} change-view - Emitted when navigating to different views
 */

import generateHmacSignature from "@/utils/utils";
import { buildApiUrl } from "@/config/api";
import { useAdminAuthStore } from "@/authen/adminAuth";
import { ADMIN_GATEWAY_PATH, ADMIN_WORKSPACE_PATH } from "@/router/adminPaths";

/**
 * Sport categories configuration
 * Matches backend CATEGORY_CLASS_MAP / CATEGORY_DISPLAY_NAMES
 * (application/routes/main/products.py)
 * @constant {Array<{id: string, name: string}>}
 */
const CATEGORIES = [
  { id: "1000", name: "Football" },
  { id: "2000", name: "Gym & Fitness" },
  { id: "3000", name: "Athletics & Running" },
  { id: "4000", name: "Basketball" },
  { id: "5000", name: "Volleyball" },
  { id: "6000", name: "Rugby" },
  { id: "7000", name: "Racket Sports" },
  { id: "8000", name: "Cricket" },
  { id: "9000", name: "Golf" },
  { id: "10000", name: "Swimming" },
  { id: "11000", name: "Cycling" },
  { id: "13000", name: "Archery" },
  { id: "14000", name: "Hockey" },
  { id: "15000", name: "Combat Sports" },
  { id: "16000", name: "Equestrian" },
  { id: "17000", name: "Adventure & Outdoor" },
  { id: "19000", name: "Niche & Regional Games" },
];

/**
 * Roles allowed to create products
 * @constant {Array<string>}
 */
const ALLOWED_CREATE_ROLES = ["support", "super_admin"];

/**
 * Subcategory mapping for product creation
 * Maps subcategory keys (stored in DB) to display names (shown to users)
 * Matches subcategoryDisplayMap from product_comp.vue
 * @constant {Object<string, string>}
 */
const SUBCATEGORY_OPTIONS = {
  "footwear": "Footwear",
  "gloves": "Gloves",
  "balls": "Balls",
  "team-kits": "Team Kits",
  "protective-gear": "Protective Gear",
  "training": "Training",
  "field-equipments": "Field Equipment",
  "court-equipment": "Court Equipment",
  "throwing-gear": "Throwing Gear",
  "timing and measurement": "Timing and Measurement",
  "equipment": "Equipment",
  "badminton": "Badminton",
  "table-tennis": "Table Tennis",
  "lawn-tennis": "Lawn Tennis",
  "bat-n-balls": "Bat and Balls",
  "wicket-n-stumps": "Wicket and Stumps",
  "bow": "Bow",
  "arrow-n-quiver": "Arrow and Quiver",
  "target-n-stands": "Target and Stands",
  "swimwear": "Swimwear",
  "safety-gear": "Safety Gear",
  "stick-n-balls": "Stick and Balls",
  "goalkeeper-kits": "Goalkeeper Kits",
  "uniform-n-belts": "Uniform and Belts",
  "bicycle": "Bicycle",
  "apparel": "Apparel",
  "accessories": "Accessories",
  "rider-apparel": "Rider Apparel",
  "tack": "Tack",
  "horse-care": "Horse Care",
  "club-n-balls": "Club and Balls",
  "bags-n-trolley": "Bags and Trolley",
  "climbing-gear": "Climbing Gear",
  "essential-accessories": "Essential Accessories",
  "african-games": "African Games",
  "motorsport": "Motorsport",
  "handball": "Handball",
  "strength": "Strength",
  "cardio": "Cardio",
  "recovery-n-mobility": "Recovery and Mobility",
};

/** Max size for thumbnail and each gallery image (client-side guard) */
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
/** Max number of additional gallery images (non-footwear) */
const MAX_IMAGE_COUNT = 5;
/** Max footwear colorway images (up to 6 colors × 4 views) */
const MAX_FOOTWEAR_IMAGE_COUNT = 24;
/** Apparel views. Add a name here to require it for every color. */
const APPAREL_VIEWS = ["undressed", "dressed"];
const MAX_APPAREL_COLORS = 6;
const MAX_APPAREL_IMAGE_COUNT = MAX_APPAREL_COLORS * APPAREL_VIEWS.length;
const FOOTBALL_CATEGORY_ID = "1000";
const FOOTWEAR_SUBCATEGORY = "footwear";
const APPAREL_SUBCATEGORY = "apparel";
const FOOTWEAR_BRANDS = [
  "Nike",
  "Adidas",
  "Puma",
  "New Balance",
  "Under Armour",
  "Mizuno",
  "Umbro",
  "Castore",
  "Asics",
  "Skechers",
];
const FOOTWEAR_VIEWS = ["main", "top", "bottom", "pair"];
const FOOTWEAR_FILENAME_RE =
  /^(.+)-#([0-9a-fA-F]{3,8})-#([0-9a-fA-F]{3,8})-(main|top|bottom|pair)(?:\.[^.]+)?$/i;
const APPAREL_FILENAME_RE = new RegExp(
  `^(.+)-#([0-9a-fA-F]{3,8})-(${APPAREL_VIEWS.join("|")})(?:\\.[^.]+)?$`,
  "i"
);

function formatFileSize(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function parseFootwearFilename(filename) {
  if (!filename || typeof filename !== "string") return null;
  const base = filename.split(/[/\\]/).pop() || filename;
  const match = base.match(FOOTWEAR_FILENAME_RE);
  if (!match) return null;
  const primary = match[2].toLowerCase();
  const secondary = match[3].toLowerCase();
  return {
    stem: match[1],
    primary,
    secondary,
    view: match[4].toLowerCase(),
    colorId: `${primary}-${secondary}`,
  };
}

function validateFootwearColorwayFiles(files) {
  const parsed = files.map((file) => ({
    file,
    meta: parseFootwearFilename(file.name),
  }));
  const namedCount = parsed.filter((item) => item.meta).length;
  if (namedCount === 0) {
    return null;
  }
  if (namedCount !== files.length) {
    return (
      "Mixed footwear filenames. Name all gallery images as " +
      "name-#primary-#secondary-view (main|top|bottom|pair), or use plain images only."
    );
  }

  const byColor = {};
  for (const { file, meta } of parsed) {
    const bucket = byColor[meta.colorId] || {};
    if (bucket[meta.view]) {
      return (
        `Duplicate view "${meta.view}" for color #${meta.primary}/#${meta.secondary}. ` +
        "Each color needs unique main, top, bottom, and pair files."
      );
    }
    bucket[meta.view] = file;
    byColor[meta.colorId] = bucket;
  }

  for (const colorId of Object.keys(byColor)) {
    const views = byColor[colorId];
    const missing = FOOTWEAR_VIEWS.filter((view) => !views[view]);
    if (missing.length) {
      const [primary, secondary] = colorId.split("-");
      return (
        `Color #${primary}/#${secondary} is missing view(s): ${missing.join(", ")}. ` +
        "Provide all four: main, top, bottom, pair."
      );
    }
  }
  return null;
}

function parseApparelFilename(filename) {
  if (!filename || typeof filename !== "string") return null;
  const base = filename.split(/[/\\]/).pop() || filename;
  const match = base.match(APPAREL_FILENAME_RE);
  if (!match) return null;
  const primary = match[2].toLowerCase();
  return {
    stem: match[1],
    primary,
    view: match[3].toLowerCase(),
    colorId: primary,
  };
}

function validateApparelColorFiles(files) {
  const parsed = files.map((file) => ({
    file,
    meta: parseApparelFilename(file.name),
  }));
  const namedCount = parsed.filter((item) => item.meta).length;
  if (namedCount === 0) {
    return null;
  }
  if (namedCount !== files.length) {
    return (
      "Mixed apparel filenames. Name all gallery images as " +
      `nameofapparel-#primarycolor-view (${APPAREL_VIEWS.join("|")}, ` +
      "for example training-tee-#1a1a1a-undressed), or use plain images only."
    );
  }

  const byColor = {};
  for (const { meta } of parsed) {
    const bucket = byColor[meta.colorId] || {};
    if (bucket[meta.view]) {
      return (
        `Duplicate view "${meta.view}" for color #${meta.primary}. ` +
        `Each color needs unique files: ${APPAREL_VIEWS.join(", ")}.`
      );
    }
    bucket[meta.view] = true;
    byColor[meta.colorId] = bucket;
  }

  for (const colorId of Object.keys(byColor)) {
    const views = byColor[colorId];
    const missing = APPAREL_VIEWS.filter((view) => !views[view]);
    if (missing.length) {
      return (
        `Color #${colorId} is missing view(s): ${missing.join(", ")}. ` +
        `Provide all of: ${APPAREL_VIEWS.join(", ")}.`
      );
    }
  }
  return null;
}

export default {
  name: "CreateProduct",
  data() {
    /**
     * Component data
     * @returns {Object}
     */
    return {
      productCreated: false,
      isLoading: false,
      showErrorModal: false,
      errorMessage: "",
      form: {
        name: "",
        sub_category: "",
        brand: "",
        category_id: "",
        surface_type: "",
        short_description: "",
        description: "",
        price: null,
        cost_price: null,
        currency: "KES",
        stock_quantity: 0,
        low_stock_threshold: 10,
        is_active: true,
        images: [],
        thumbnail: "",
        weight: null,
        size: "",
        returnable: true,
      },
      selectedImages: [],
      selectedThumbnail: null,
      categories: CATEGORIES,
      subcategories: SUBCATEGORY_OPTIONS,
      footwearBrands: FOOTWEAR_BRANDS,
      basicFields: [
        { label: "Product Name", model: "name", type: "text", required: true },
        { label: "Brand", model: "brand", type: "text", required: true },
        { label: "Sub Category", model: "sub_category", type: "select" },
        { label: "Short Description", model: "short_description", type: "text", maxlength: 255, required: true },
        { label: "Full Description", model: "description", type: "textarea", required: true },
      ],
      pricingFields: [
        { label: "Price", model: "price", type: "number", required: true },
        { label: "Cost Price", model: "cost_price", type: "number" },
        { label: "Currency", model: "currency", type: "text", required: true },
      ],
      inventoryFields: [
        { label: "Stock Quantity", model: "stock_quantity", type: "number" },
        { label: "Low Stock Threshold", model: "low_stock_threshold", type: "number" },
        { label: "Active", model: "is_active", type: "checkbox" },
      ],
      otherFields: [
        { label: "Weight (grams)", model: "weight", type: "number" },
        { label: "Size", model: "size", type: "text", placeholder: "e.g. L, XL, 42, 10.5, One Size" },
        { label: "Returnable", model: "returnable", type: "checkbox" },
      ],
    };
  },
  computed: {
    /**
     * Checks if current user has permission to create products
     * @returns {boolean}
     */
    canCreateProduct() {
      const store = useAdminAuthStore();
      const role = (store.admin?.role || "").toLowerCase();
      return ALLOWED_CREATE_ROLES.includes(role);
    },
    isFootwear() {
      return (
        String(this.form.sub_category || "").toLowerCase() === FOOTWEAR_SUBCATEGORY
      );
    },
    isApparel() {
      return (
        String(this.form.sub_category || "").toLowerCase() === APPAREL_SUBCATEGORY
      );
    },
    isFootballFootwear() {
      return (
        String(this.form.category_id || "") === FOOTBALL_CATEGORY_ID &&
        this.isFootwear
      );
    },
    maxFileSizeLabel() {
      return formatFileSize(MAX_FILE_SIZE_BYTES);
    },
    uploadSizeBytes() {
      let total = 0;
      if (this.selectedThumbnail instanceof File) {
        total += this.selectedThumbnail.size;
      }
      this.selectedImages.forEach((file) => {
        if (file instanceof File) total += file.size;
      });
      return total;
    },
    uploadSizeSummary() {
      return formatFileSize(this.uploadSizeBytes);
    },
    maxImageCount() {
      if (this.isFootwear) return MAX_FOOTWEAR_IMAGE_COUNT;
      if (this.isApparel) return MAX_APPAREL_IMAGE_COUNT;
      return MAX_IMAGE_COUNT;
    },
    maxApparelColors() {
      return MAX_APPAREL_COLORS;
    },
  },
  watch: {
    "form.category_id"() {
      this.syncFootwearFields();
    },
    "form.sub_category"() {
      this.syncFootwearFields();
    },
  },
  methods: {
    formatFileSize,

    syncFootwearFields() {
      if (!this.isFootballFootwear) {
        this.form.surface_type = "";
      } else if (this.form.brand && !this.footwearBrands.includes(this.form.brand)) {
        this.form.brand = "";
      }
    },

    showUploadError(message) {
      this.errorMessage = message;
      this.showErrorModal = true;
    },

    validateImageFile(file, label = "Image") {
      if (!(file instanceof File)) {
        return `${label} is not a valid file.`;
      }
      if (!file.type.startsWith("image/")) {
        return `${label} must be an image file (JPEG, PNG, WebP, etc.).`;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        return `${label} "${file.name}" is ${formatFileSize(file.size)}. Maximum per image is ${formatFileSize(MAX_FILE_SIZE_BYTES)}.`;
      }
      return null;
    },

    validateMediaUploads() {
      const errors = [];

      if (!this.selectedThumbnail) {
        errors.push("Please upload a thumbnail image");
      } else {
        const thumbError = this.validateImageFile(this.selectedThumbnail, "Thumbnail");
        if (thumbError) errors.push(thumbError);
      }

      const maxImages = this.maxImageCount;
      if (this.selectedImages.length > maxImages) {
        errors.push(`You can upload at most ${maxImages} additional images.`);
      }

      this.selectedImages.forEach((file, index) => {
        const fileError = this.validateImageFile(file, `Image ${index + 1}`);
        if (fileError) errors.push(fileError);
      });

      if (this.isFootwear && this.selectedImages.length > 0) {
        const footwearError = validateFootwearColorwayFiles(this.selectedImages);
        if (footwearError) errors.push(footwearError);
      }

      if (this.isApparel && this.selectedImages.length > 0) {
        const apparelError = validateApparelColorFiles(this.selectedImages);
        if (apparelError) errors.push(apparelError);
      }

      return errors;
    },

    /**
     * Triggers the thumbnail file input click event
     */
    triggerThumbnailUpload() {
      if (this.isLoading || !this.canCreateProduct) return;
      if (this.$refs.thumbnailInput) {
        this.$refs.thumbnailInput.click();
      } else {
        this.errorMessage = "File upload system error. Please refresh the page.";
        this.showErrorModal = true;
      }
    },
    
    /**
     * Triggers the additional images file input click event
     */
    triggerImagesUpload() {
      if (this.isLoading || !this.canCreateProduct) return;
      if (this.$refs.imagesInput) {
        this.$refs.imagesInput.click();
      } else {
        this.errorMessage = "File upload system error. Please refresh the page.";
        this.showErrorModal = true;
      }
    },
    
    /**
     * Handles additional images file selection
     * @param {Event} event - File input change event
     */
    onImagesChange(event) {
      if (!event?.target?.files) {
        this.showUploadError("Invalid file selection. Please try again.");
        return;
      }

      const files = Array.from(event.target.files);
      if (files.length === 0) {
        this.selectedImages = [];
        return;
      }

      if (files.length > this.maxImageCount) {
        this.showUploadError(
          `You selected ${files.length} images. Maximum allowed is ${this.maxImageCount}. Choose fewer files.`
        );
        event.target.value = "";
        return;
      }

      const fileErrors = [];
      files.forEach((file, index) => {
        const err = this.validateImageFile(file, `Image ${index + 1}`);
        if (err) fileErrors.push(err);
      });

      if (fileErrors.length > 0) {
        this.showUploadError(fileErrors.join(" "));
        this.selectedImages = [];
        event.target.value = "";
        return;
      }

      if (this.isFootwear) {
        const footwearError = validateFootwearColorwayFiles(files);
        if (footwearError) {
          this.showUploadError(footwearError);
          this.selectedImages = [];
          event.target.value = "";
          return;
        }
      }

      if (this.isApparel) {
        const apparelError = validateApparelColorFiles(files);
        if (apparelError) {
          this.showUploadError(apparelError);
          this.selectedImages = [];
          event.target.value = "";
          return;
        }
      }

      this.selectedImages = files;
    },
    
    /**
     * Handles thumbnail file selection with validation
     * @param {Event} event - File input change event
     */
    onThumbnailChange(event) {
      if (!event?.target?.files?.length) {
        this.selectedThumbnail = null;
        return;
      }
      
      const file = event.target.files[0];
      if (!file) {
        this.selectedThumbnail = null;
        return;
      }
      
      // Validate file type
      if (!file.type.startsWith("image/")) {
        this.showUploadError("Please select a valid image file for the thumbnail.");
        this.selectedThumbnail = null;
        if (this.$refs.thumbnailInput) {
          this.$refs.thumbnailInput.value = "";
        }
        return;
      }

      const thumbError = this.validateImageFile(file, "Thumbnail");
      if (thumbError) {
        this.showUploadError(thumbError);
        this.selectedThumbnail = null;
        if (this.$refs.thumbnailInput) {
          this.$refs.thumbnailInput.value = "";
        }
        return;
      }

      this.selectedThumbnail = file;
    },
    
    /**
     * Main product creation method
     * Validates form data, generates HMAC signature, and submits to API
     * @async
     * @returns {Promise<void>}
     */
    async createProduct() {
      if (!this.canCreateProduct) {
        this.errorMessage = "You do not have permission to create products. Only support and super admin can create products.";
        this.showErrorModal = true;
        return;
      }
      
      // Validate required fields
      const validationErrors = [];
      
      if (!this.form.name?.trim()) {
        validationErrors.push("Product name is required");
      }
      if (!this.form.category_id) {
        validationErrors.push("Please select a product category");
      }
      validationErrors.push(...this.validateMediaUploads());
      if (!this.form.price || this.form.price <= 0) {
        validationErrors.push("Please enter a valid product price (greater than 0)");
      }
      if (!this.form.short_description?.trim()) {
        validationErrors.push("Short description is required");
      }
      if (!this.form.description?.trim()) {
        validationErrors.push("Full description is required");
      }
      if (!this.form.brand?.trim()) {
        validationErrors.push("Brand is required");
      }
      if (this.isFootballFootwear && !this.form.surface_type) {
        validationErrors.push("Please select turf (TF) or firm ground (FG)");
      }
      
      if (validationErrors.length > 0) {
        this.errorMessage = validationErrors.join(". ") + ".";
        this.showErrorModal = true;
        return;
      }

      // Get admin store and validate authentication
      const adminStore = useAdminAuthStore();
      if (!adminStore) {
        this.errorMessage = "Authentication system error. Please refresh the page and try again.";
        this.showErrorModal = true;
        return;
      }
      
      // Check if token is still valid
      try {
        adminStore.checkToken();
        if (!adminStore.isAuthenticated) {
          this.errorMessage = "Your session has expired. Please sign in again.";
          this.showErrorModal = true;
          return;
        }
      } catch (tokenError) {
        this.errorMessage = "Session validation failed. Please sign in again.";
        this.showErrorModal = true;
        return;
      }
      
      this.isLoading = true;
      this.showErrorModal = false;
      this.errorMessage = "";

      try {
        const payloadForHmac = {
          ...this.form,
          images: [],
          thumbnail: "",
          color_variants: null,
          cost_price: this.form.cost_price ?? null,
          surface_type: this.isFootballFootwear ? this.form.surface_type : null,
        };
        
        const hmacPayload = generateHmacSignature(payloadForHmac);
        if (!hmacPayload || typeof hmacPayload !== "object") {
          throw new Error("Failed to generate security signature. Please try again.");
        }

        // Create FormData
        const formData = new FormData();
        formData.append("payload", JSON.stringify(hmacPayload));
        
        if (this.selectedThumbnail instanceof File) {
          formData.append("thumbnail", this.selectedThumbnail);
        }
        
        if (Array.isArray(this.selectedImages)) {
          this.selectedImages.forEach((file) => {
            if (file instanceof File) {
              formData.append("images", file);
            }
          });
        }

        // Get authentication token
        const rawToken = adminStore.accessToken || localStorage.getItem("adminAccessToken");
        const token = typeof rawToken === "string" ? rawToken.trim() : "";
        if (!token) {
          throw new Error("You must be signed in as admin to create products. Please sign in and try again.");
        }

        // Build API URL
        const apiUrl = buildApiUrl("/post/create_product");
        if (!apiUrl) {
          throw new Error("Unable to connect to the server. Please check your connection and try again.");
        }

        // Make API request
        let res;
        try {
          res = await fetch(apiUrl, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          });
        } catch (networkErr) {
          if (networkErr instanceof TypeError) {
            throw new Error(
              "Unable to reach the server. Check your internet connection. If images are large, compress them and try again."
            );
          }
          throw networkErr;
        }

        // Parse response
        let data = {};
        try {
          const responseText = await res.text();
          if (responseText) {
            data = JSON.parse(responseText);
          }
        } catch (jsonError) {
          if (res.ok) {
            data = { success: true };
          } else if (res.status === 413) {
            throw new Error(
              `Upload is too large for the server. Thumbnail and each additional image must be under ${formatFileSize(MAX_FILE_SIZE_BYTES)} (max ${MAX_IMAGE_COUNT} additional images), then try again.`
            );
          } else {
            throw new Error(`Server error (${res.status}). Please try again later.`);
          }
        }

        // Handle errors
        if (!res.ok) {
          if (res.status === 413) {
            throw new Error(
              `Upload is too large for the server. Thumbnail and each additional image must be under ${formatFileSize(MAX_FILE_SIZE_BYTES)} (max ${MAX_IMAGE_COUNT} additional images), then try again.`
            );
          }
          if (res.status === 401) {
            // Clear expired token
            try {
              adminStore.logout();
            } catch (logoutError) {
              console.error("Logout error:", logoutError);
            }
            
            const errorMsg = data.detail || data.message || "Session expired or not authorized";
            const isExpired = errorMsg.toLowerCase().includes("expired") || errorMsg.toLowerCase().includes("expire");
            
            if (isExpired) {
              // Redirect to login page after a short delay
              setTimeout(() => {
                try {
                  this.$router.push({ 
                    name: "AdminLoginPanel", 
                    query: { redirect: this.$route?.fullPath || ADMIN_WORKSPACE_PATH } 
                  }).catch(() => {
                    window.location.href = ADMIN_GATEWAY_PATH;
                  });
                } catch {
                  window.location.href = ADMIN_GATEWAY_PATH;
                }
              }, 2000);
              throw new Error("Your session has expired. Redirecting to login...");
            }
            throw new Error("Session expired or not authorized. Please sign in again as admin.");
          }
          
          // Handle validation errors
          if (res.status === 400 || res.status === 422) {
            const detail = Array.isArray(data.detail) 
              ? data.detail.map((e) => e.msg || e).join("; ") 
              : (data.detail || data.message || "Invalid product data. Please check your input.");
            throw new Error(detail);
          }
          
          // Handle other errors
          const detail = Array.isArray(data.detail) 
            ? data.detail.map((e) => e.msg || e).join("; ") 
            : (data.detail || data.message || `Failed to create product (${res.status}). Please try again.`);
          throw new Error(detail);
        }

        // Success
        this.productCreated = true;
      } catch (err) {
        const userFriendlyMessage = err.message || "An unexpected error occurred while creating the product. Please try again.";
        this.errorMessage = userFriendlyMessage;
        this.showErrorModal = true;
        console.error("Product creation error:", err);
      } finally {
        this.isLoading = false;
      }
    },
    
    /**
     * Closes the error modal
     */
    closeErrorModal() {
      this.showErrorModal = false;
      this.errorMessage = "";
    },
    
    /**
     * Retries product creation by closing error modal
     * User can then fix errors and submit again
     */
    retryCreate() {
      this.closeErrorModal();
    },
    
    /**
     * Resets the entire form to initial state
     */
    resetForm() {
      this.productCreated = false;
      this.showErrorModal = false;
      this.errorMessage = "";
      this.form = {
        name: "",
        sub_category: "",
        brand: "",
        category_id: "",
        surface_type: "",
        short_description: "",
        description: "",
        price: null,
        cost_price: null,
        currency: "KES",
        stock_quantity: 0,
        low_stock_threshold: 10,
        is_active: true,
        images: [],
        thumbnail: "",
        weight: null,
        size: "",
        returnable: true,
      };
      this.selectedImages = [];
      this.selectedThumbnail = null;
      
      // Reset file inputs
      if (this.$refs.thumbnailInput) {
        this.$refs.thumbnailInput.value = '';
      }
      if (this.$refs.imagesInput) {
        this.$refs.imagesInput.value = '';
      }
    },
    
    /**
     * Navigates to products listing page
     */
    goToProducts() {
      try {
        this.$router.push('/products').catch(() => {
          window.location.href = '/products';
        });
      } catch {
        window.location.href = '/products';
      }
    }
  },
};
</script>

<style scoped>
/* === Base Styles === */
.create-product {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  background: var(--background-color2);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  font-family: "Poppins", sans-serif;
  min-height: 100%;
  overflow: visible;
  position: relative;
  display: block;
}

/* Form wrapper for proper scrolling */
.form-wrapper {
  width: 100%;
  overflow: visible;
  min-height: fit-content;
  display: block;
}

.product-form {
  width: 100%;
  overflow: visible;
  min-height: fit-content;
  padding-bottom: 3rem; /* Extra padding at bottom for better scrolling */
  display: block;
}

/* === Form Header === */
.form-header {
  text-align: center;
  margin-bottom: 2.5rem;
}

.form-header h1 {
  font-size: 2.2rem;
  color: var(--text-color1);
  margin-bottom: 0.5rem;
  font-weight: 700;
}

.form-subtitle {
  color: var(--text-color2);
  font-size: 1rem;
  opacity: 0.8;
}

.permission-warning {
  margin-top: 12px;
  padding: 12px 16px;
  background: rgba(255, 71, 87, 0.12);
  border: 1px solid rgba(255, 71, 87, 0.3);
  border-radius: 8px;
  color: #ff4757;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* === Form Sections === */
.form-section {
  background: var(--background-color1);
  padding: 1.8rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  border: 1px solid var(--border-color-hover);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.form-section:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(var(--border-color-hover-rgb), 0.1);
}

.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 1.5rem;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--border-color-hover);
}

.section-header i {
  color: var(--border-color-hover);
  font-size: 1.2rem;
}

.section-header h3 {
  color: var(--text-color1);
  font-size: 1.3rem;
  font-weight: 600;
  margin: 0;
}

/* === Form Grid === */
.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

label {
  font-size: 0.95rem;
  color: var(--text-color1);
  margin-bottom: 8px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}

.required {
  color: #ff4757;
  font-size: 1.2rem;
}

/* === Input Styles === */
input, textarea, select {
  width: 100%;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid var(--border-color-hover);
  font-size: 0.95rem;
  color: var(--text-color1);
  background: var(--background-color2);
  outline: none;
  transition: all 0.3s ease;
}

input:focus, textarea:focus, select:focus {
  border-color: var(--border-color-hover);
  border-width: 1px;
  box-shadow: 0 0 0 3px rgba(var(--border-color-hover-rgb), 0.1);
}

input:disabled, textarea:disabled, select:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: rgba(0, 0, 0, 0.05);
}

textarea {
  min-height: 120px;
  resize: vertical;
}

/* === Input with Icons === */
.input-with-icon {
  position: relative;
}

.input-with-icon i {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-color2);
  font-size: 1rem;
}

.input-with-icon input, .input-with-icon select {
  padding-right: 45px;
}

/* === File Upload === */
.file-upload-area {
  border: 1px dashed var(--border-color-hover);
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(var(--border-color-hover-rgb), 0.05);
}

.file-upload-area:hover:not(.disabled) {
  border-color: var(--border-color-hover);
  border-style: solid;
  background: rgba(var(--border-color-hover-rgb), 0.1);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(var(--border-color-hover-rgb), 0.15);
}

.file-upload-area.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.file-upload-area i {
  font-size: 2rem;
  color: var(--border-color-hover);
  margin-bottom: 12px;
}

.file-upload-area p {
  color: var(--text-color2);
  margin: 0;
}

.file-name {
  color: var(--text-color1) !important;
  font-weight: 500;
}

.upload-hint {
  margin: 8px 0 0;
  font-size: 0.82rem;
  color: var(--text-color2);
  line-height: 1.4;
}

.upload-hint code {
  font-size: 0.78rem;
  word-break: break-all;
}

.upload-summary {
  margin: 12px 0 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-color1);
}

.upload-summary--over {
  color: #c62828;
}

/* === Checkbox Styles === */
.checkbox-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  margin-bottom: 0;
  font-weight: normal;
}

.checkbox-wrapper input[type="checkbox"] {
  display: none;
}

.checkmark {
  width: 20px;
  height: 20px;
  border: 1px solid var(--border-color-hover);
  border-radius: 4px;
  position: relative;
  transition: all 0.3s ease;
}

.checkbox-wrapper input[type="checkbox"]:checked + .checkbox-label .checkmark {
  background: var(--border-color-hover);
  border-color: var(--border-color-hover);
}

.checkbox-wrapper input[type="checkbox"]:checked + .checkbox-label .checkmark::after {
  content: '';
  position: absolute;
  left: 6px;
  top: 2px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

/* === Form Actions === */
.form-actions {
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid var(--border-color-hover);
}

.submit-btn, .cancel-btn, .continue-btn, .view-btn {
  flex: 1;
  padding: 16px 24px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: none;
}

.submit-btn {
  background: linear-gradient(135deg, var(--border-color-hover), var(--border-color-hover1));
  color: white;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(var(--border-color-hover-rgb), 0.3);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.submit-btn.loading {
  background: linear-gradient(135deg, #6c757d, #495057);
}

.cancel-btn {
  background: #6c757d;
  color: #ffffff;
  border: 1px solid #6c757d;
}

.cancel-btn:hover:not(:disabled) {
  background: #5a6268;
  border-color: #5a6268;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(108, 117, 125, 0.3);
}

/* === Success State === */
.ack-container {
  text-align: center;
  padding: 4rem 2rem;
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.05), rgba(46, 125, 50, 0.05));
  border-radius: 16px;
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  border: 2px solid rgba(76, 175, 80, 0.2);
  box-shadow: 0 8px 32px rgba(76, 175, 80, 0.15);
  animation: successFadeIn 0.5s ease;
}

@keyframes successFadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.success-animation {
  position: relative;
  width: 120px;
  height: 120px;
  margin-bottom: 2.5rem;
}

.success-checkmark {
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, #4caf50, #2e7d32);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
  box-shadow: 0 8px 24px rgba(76, 175, 80, 0.4);
  animation: checkmarkScale 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

@keyframes checkmarkScale {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

.success-checkmark i {
  color: white;
  font-size: 3.5rem;
  animation: checkmarkFadeIn 0.5s ease 0.3s both;
}

@keyframes checkmarkFadeIn {
  from {
    opacity: 0;
    transform: scale(0);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.success-ripple {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 120px;
  height: 120px;
  border: 3px solid #4caf50;
  border-radius: 50%;
  animation: ripple 2s infinite;
  opacity: 0;
}

.success-ripple.delay-1 {
  animation-delay: 0.7s;
}

.success-ripple.delay-2 {
  animation-delay: 1.4s;
}

@keyframes ripple {
  0% {
    transform: translate(-50%, -50%) scale(0.8);
    opacity: 0.8;
  }
  100% {
    transform: translate(-50%, -50%) scale(2);
    opacity: 0;
  }
}

.ack-container h2 {
  font-size: 2.5rem;
  color: #2e7d32;
  margin-bottom: 1rem;
  font-weight: 700;
  animation: textFadeIn 0.6s ease 0.4s both;
}

@keyframes textFadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.ack-container p {
  color: var(--text-color2);
  font-size: 1.2rem;
  margin-bottom: 2rem;
  max-width: 500px;
  line-height: 1.6;
  animation: textFadeIn 0.6s ease 0.6s both;
}

.success-details {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 2.5rem;
  max-width: 400px;
  width: 100%;
  animation: textFadeIn 0.6s ease 0.8s both;
}

.success-detail-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: rgba(76, 175, 80, 0.1);
  border-radius: 8px;
  border-left: 3px solid #4caf50;
  color: #2e7d32;
  font-size: 0.95rem;
  text-align: left;
}

.success-detail-item i {
  color: #4caf50;
  font-size: 1rem;
}

.success-actions {
  display: flex;
  gap: 1rem;
  max-width: 500px;
  width: 100%;
  animation: textFadeIn 0.6s ease 1s both;
}

.continue-btn {
  background: linear-gradient(135deg, var(--border-color-hover), var(--border-color-hover1));
  color: white;
}

.view-btn {
  background: #ffffff;
  color: #2e7d32;
  border: 2px solid #4caf50;
}

.continue-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(76, 175, 80, 0.4);
}

.view-btn:hover {
  transform: translateY(-2px);
  background: #f1f8f4;
  box-shadow: 0 8px 20px rgba(76, 175, 80, 0.2);
}

/* === Error Modal === */
.error-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.error-modal-content {
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 500px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalSlideIn 0.3s ease;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.error-modal-header {
  background: linear-gradient(135deg, #ff4757, #ff6b81);
  color: white;
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.error-modal-icon {
  font-size: 2rem;
}

.error-modal-header h3 {
  font-size: 1.4rem;
  margin: 0;
  flex: 1;
}

.error-modal-close {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  transition: background 0.2s ease;
}

.error-modal-close:hover {
  background: rgba(255, 255, 255, 0.3);
}

.error-modal-body {
  padding: 32px;
}

.error-modal-body p {
  color: #333;
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 24px;
  text-align: center;
}

.error-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.error-retry-btn, .error-close-btn {
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-size: 1rem;
  transition: all 0.2s ease;
}

.error-retry-btn {
  background: #007bff;
  color: white;
  display: flex;
  align-items: center;
  gap: 8px;
}

.error-retry-btn:hover {
  background: #0056b3;
  transform: translateY(-1px);
}

.error-close-btn {
  background: #6c757d;
  color: white;
}

.error-close-btn:hover {
  background: #545b62;
}

/* === Responsive Design === */

/* Large tablets and small desktops (1024px and below) */
@media (max-width: 1024px) {
  .create-product {
    padding: 1.5rem;
    margin: 0;
    max-width: 100%;
  }
  
  .form-grid {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.25rem;
  }
  
  .form-section {
    padding: 1.5rem;
  }
}

/* Tablets (768px and below) */
@media (max-width: 768px) {
  .create-product {
    padding: 1rem;
    margin: 0 auto;
    border-radius: 12px;
  }
  
  .form-header {
    margin-bottom: 2rem;
  }
  
  .form-header h1 {
    font-size: clamp(1.5rem, 5vw, 1.8rem);
  }
  
  .form-subtitle {
    font-size: 0.9rem;
  }
  
  .form-section {
    padding: 1.2rem;
    margin-bottom: 1.25rem;
  }
  
  .section-header {
    margin-bottom: 1.25rem;
    padding-bottom: 0.75rem;
  }
  
  .section-header h3 {
    font-size: 1.1rem;
  }
  
  .form-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .form-actions {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .submit-btn, .cancel-btn {
    width: 100%;
    padding: 14px 20px;
  }
  
  .success-actions {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .continue-btn, .view-btn {
    width: 100%;
  }
  
  .file-upload-area {
    padding: 1.5rem;
  }
  
  .file-upload-area i {
    font-size: 1.5rem;
  }
  
  .error-modal-content {
    max-width: 95%;
    margin: 1rem;
  }
}

/* Mobile phones (640px and below) */
@media (max-width: 640px) {
  .create-product {
    padding: 0.75rem;
    margin: 0 auto;
    border-radius: 8px;
  }
  
  .form-header h1 {
    font-size: clamp(1.3rem, 6vw, 1.6rem);
    margin-bottom: 0.5rem;
  }
  
  .form-subtitle {
    font-size: 0.85rem;
  }
  
  .permission-warning {
    font-size: 0.85rem;
    padding: 10px 12px;
  }
  
  .form-section {
    padding: 1rem;
    margin-bottom: 1rem;
  }
  
  .section-header {
    gap: 8px;
    margin-bottom: 1rem;
  }
  
  .section-header i {
    font-size: 1rem;
  }
  
  .section-header h3 {
    font-size: 1rem;
  }
  
  .form-grid {
    gap: 0.875rem;
  }
  
  label {
    font-size: 0.875rem;
    margin-bottom: 6px;
  }
  
  input, textarea, select {
    padding: 10px 14px;
    font-size: 0.9rem;
  }
  
  textarea {
    min-height: 100px;
  }
  
  .file-upload-area {
    padding: 1.25rem;
  }
  
  .file-upload-area i {
    font-size: 1.25rem;
    margin-bottom: 8px;
  }
  
  .file-upload-area p {
    font-size: 0.85rem;
  }
  
  .checkbox-wrapper {
    margin-top: 15px;
  }
  
  .form-actions {
    margin-top: 1.5rem;
    padding-top: 1.5rem;
  }
  
  .ack-container {
    padding: 2rem 1rem;
    min-height: 60vh;
  }
  
  .ack-container h2 {
    font-size: clamp(1.5rem, 6vw, 1.8rem);
  }
  
  .ack-container p {
    font-size: 1rem;
  }
  
  .success-animation {
    width: 100px;
    height: 100px;
    margin-bottom: 2rem;
  }
  
  .success-checkmark {
    width: 100px;
    height: 100px;
  }
  
  .success-checkmark i {
    font-size: 3rem;
  }
  
  .success-details {
    margin-bottom: 2rem;
  }
  
  .success-detail-item {
    font-size: 0.85rem;
    padding: 0.625rem 0.875rem;
  }
  
  .error-modal-overlay {
    padding: 10px;
  }
  
  .error-modal-content {
    max-width: 100%;
    border-radius: 12px;
  }
  
  .error-modal-header {
    padding: 18px;
  }
  
  .error-modal-header h3 {
    font-size: 1.2rem;
  }
  
  .error-modal-icon {
    font-size: 1.5rem;
  }
  
  .error-modal-body {
    padding: 20px;
  }
  
  .error-modal-body p {
    font-size: 1rem;
    margin-bottom: 20px;
  }
  
  .error-actions {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .error-retry-btn, .error-close-btn {
    width: 100%;
    padding: 10px 20px;
  }
}

/* Small mobile phones (480px and below) */
@media (max-width: 480px) {
  .create-product {
    padding: 0.5rem;
    margin: 0 auto;
    border-radius: 8px;
  }
  
  .form-header {
    margin-bottom: 1.5rem;
  }
  
  .form-header h1 {
    font-size: clamp(1.2rem, 7vw, 1.5rem);
  }
  
  .form-section {
    padding: 0.875rem;
    border-radius: 8px;
  }
  
  .section-header h3 {
    font-size: 0.95rem;
  }
  
  .form-grid {
    gap: 0.75rem;
  }
  
  input, textarea, select {
    padding: 8px 12px;
    font-size: 0.875rem;
  }
  
  .input-with-icon i {
    right: 12px;
    font-size: 0.9rem;
  }
  
  .input-with-icon input, .input-with-icon select {
    padding-right: 40px;
  }
  
  .file-upload-area {
    padding: 1rem;
  }
  
  .file-upload-area i {
    font-size: 1.1rem;
  }
  
  .ack-container {
    padding: 1.5rem 0.75rem;
  }
  
  .ack-container h2 {
    font-size: clamp(1.3rem, 7vw, 1.6rem);
  }
  
  .ack-container p {
    font-size: 0.95rem;
  }
  
  .success-animation {
    width: 90px;
    height: 90px;
    margin-bottom: 1.5rem;
  }
  
  .success-checkmark {
    width: 90px;
    height: 90px;
  }
  
  .success-checkmark i {
    font-size: 2.5rem;
  }
  
  .ack-container h2 {
    font-size: clamp(1.5rem, 6vw, 1.8rem);
  }
  
  .success-details {
    margin-bottom: 1.5rem;
  }
  
  .success-detail-item {
    font-size: 0.8rem;
    padding: 0.5rem 0.75rem;
  }
  
  .error-modal-header {
    padding: 16px;
  }
  
  .error-modal-header h3 {
    font-size: 1.1rem;
  }
  
  .error-modal-body {
    padding: 18px;
  }
  
  .error-modal-body p {
    font-size: 0.95rem;
  }
}

/* Extra small phones (360px and below) */
@media (max-width: 360px) {
  .create-product {
    padding: 0.5rem;
  }
  
  .form-header h1 {
    font-size: 1.2rem;
  }
  
  .form-section {
    padding: 0.75rem;
  }
  
  .section-header h3 {
    font-size: 0.9rem;
  }
  
  input, textarea, select {
    padding: 8px 10px;
    font-size: 0.85rem;
  }
  
  .file-upload-area {
    padding: 0.875rem;
  }
  
  .ack-container h2 {
    font-size: 1.3rem;
  }
  
  .error-modal-header h3 {
    font-size: 1rem;
  }
}

/* Landscape orientation on mobile */
@media (max-height: 500px) and (orientation: landscape) {
  .create-product {
    padding: 0.75rem;
  }
  
  .form-header {
    margin-bottom: 1rem;
  }
  
  .form-section {
    padding: 1rem;
    margin-bottom: 0.75rem;
  }
  
  .ack-container {
    padding: 1.5rem;
    min-height: auto;
  }
}

/* Touch device optimizations */
@media (hover: none) and (pointer: coarse) {
  .submit-btn, .cancel-btn, .continue-btn, .view-btn,
  .error-retry-btn, .error-close-btn {
    min-height: 44px; /* Minimum touch target size */
    padding: 12px 20px;
  }
  
  .file-upload-area {
    min-height: 120px;
  }
  
  input, textarea, select {
    min-height: 44px;
  }
}

/* === Transitions === */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
